import type { ContextGroup, ModuleAnswer, SectionFeedback } from "@/types/simulation-results";

import { getCorrectedVersion, getFeedbackText } from "@/lib/analysisBreakdown";
import { parseCorrectedTextTokens } from "./correctedTextModel";

export interface AnswerGroups {
    readonly contextGroups: ContextGroup[];
    readonly standaloneAnswers: ModuleAnswer[];
    readonly displayAnswers: ModuleAnswer[];
}

/** One exam section's questions plus optional LLM section feedback (R/L review). */
export interface SectionReviewBlock {
    readonly section: number;
    readonly contextGroups: ContextGroup[];
    readonly standaloneAnswers: ModuleAnswer[];
    readonly sectionFeedback: SectionFeedback | null;
}

export const sectionFeedbackRowHasContent = (row: SectionFeedback): boolean =>
    row.status === "pending"
    || row.status === "failed"
    || (row.priority_focus || "").trim().length > 0
    || (row.recommended_practice?.length ?? 0) > 0
    || (row.strength_summary || "").trim().length > 0
    || (row.weakness_summary || "").trim().length > 0
    || (row.notes_summary || "").trim().length > 0;

export interface QuestionNavigatorItem {
    readonly answer: ModuleAnswer;
    readonly number: number;
    readonly isUnanswered: boolean;
    readonly isCorrect: boolean;
}

export interface AdditionalInfoSections {
    readonly correctedVersion: string;
    readonly taskFulfillment: string;
}

export type AnswerBlankAnnotationState = "correct" | "incorrect" | "empty" | "needs-review";

export interface AnswerBlankAnnotation {
    readonly index: number;
    readonly value: string;
    readonly expected?: string;
    readonly state: AnswerBlankAnnotationState;
}

export interface AnswerTextAnnotationSegment {
    readonly text: string;
    readonly state: "plain" | "issue" | "insertion";
    readonly start: number;
    readonly end: number;
    readonly annotationIndex?: number;
    readonly correction?: string;
}

export interface AnswerAnnotationModel {
    readonly kind: "fill-in-blank" | "text";
    readonly hasInlineAnnotations: boolean;
    readonly blanks: AnswerBlankAnnotation[];
    readonly segments: AnswerTextAnnotationSegment[];
    readonly insertions: string[];
}

export type WritingBlankAnnotationState = AnswerBlankAnnotationState;
export type WritingBlankAnnotation = AnswerBlankAnnotation;
export type WritingTextAnnotationSegment = AnswerTextAnnotationSegment;
export type WritingAnswerAnnotationModel = AnswerAnnotationModel;

const toDomIdPart = (value: string): string =>
    value.replace(/[^A-Za-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "") || "answer";

export const getBlankAnnotationId = (answerId: string, blankIndex: number): string =>
    `answer-${toDomIdPart(answerId)}-blank-${blankIndex}`;

export const getTextAnnotationId = (answerId: string, annotationIndex: number): string =>
    `answer-${toDomIdPart(answerId)}-text-${annotationIndex}`;

export const getPronunciationAnnotationId = (answerId: string, wordIndex: number): string =>
    `answer-${toDomIdPart(answerId)}-pronunciation-${wordIndex}`;

export const normalizeAnswerText = (value?: string | null) => {
    if (value === null || value === undefined) return "";
    return typeof value === "string" ? value.trim() : String(value).trim();
};

export const isNoAnswerPlaceholder = (value?: string | null): boolean => {
    const normalized = normalizeAnswerText(value).toLowerCase();
    return normalized === "no answer provided." || normalized === "no answer provided";
};

export const hasSubmittedAnswer = (answer: ModuleAnswer): boolean => {
    const answerText = normalizeAnswerText(answer.answer_text);
    return (
        (answerText.length > 0 && !isNoAnswerPlaceholder(answerText)) ||
        Boolean(answer.answer_audio_base64) ||
        Boolean(answer.audio_url)
    );
};

export const getQuestionScrollTop = (
    viewportTop: number,
    viewportScrollTop: number,
    targetTop: number,
    offset = 16,
): number => Math.max(0, targetTop - viewportTop + viewportScrollTop - offset);

export const getAdditionalInfoSections = (answer: ModuleAnswer): AdditionalInfoSections => ({
    correctedVersion: normalizeAnswerText(getCorrectedVersion(answer)),
    taskFulfillment: normalizeAnswerText(getFeedbackText(answer, "task_fulfillment")),
});

const normalizeQuestionType = (questionType?: string | null): string =>
    normalizeAnswerText(questionType).toLowerCase().replace(/_/g, "-");

export const isFillInBlankAnswer = (answer: ModuleAnswer): boolean =>
    normalizeQuestionType(answer.question_type).includes("fill-in-blank");

const normalizeComparableAnswer = (value: string): string =>
    value
        .normalize("NFC")
        .toLowerCase()
        .replace(/ä/g, "ae")
        .replace(/ö/g, "oe")
        .replace(/ü/g, "ue")
        .replace(/ß/g, "ss")
        .replace(/\s+/g, " ")
        .trim();

const splitFillBlankAnswer = (answerText: string): string[] => {
    if (!answerText || isNoAnswerPlaceholder(answerText)) return [];
    try {
        const parsed = JSON.parse(answerText);
        if (Array.isArray(parsed)) {
            return parsed.map((value) => normalizeAnswerText(value));
        }
    } catch {
        // Legacy answers were persisted as a pipe-joined string.
    }
    return answerText.split(/\s*\|\s*/);
};

export const buildWritingBlankAnnotations = (
    answerText: string,
    expectedAnswers: readonly string[],
): AnswerBlankAnnotation[] => {
    const values = splitFillBlankAnswer(answerText);
    const blankCount = Math.max(values.length, expectedAnswers.length);

    return Array.from({ length: blankCount }, (_, idx) => {
        const value = normalizeAnswerText(values[idx]);
        const expected = normalizeAnswerText(expectedAnswers[idx]);
        let state: AnswerBlankAnnotationState = "needs-review";

        if (!value) {
            state = "empty";
        } else if (expected) {
            state = normalizeComparableAnswer(value) === normalizeComparableAnswer(expected)
                ? "correct"
                : "incorrect";
        }

        return {
            index: idx + 1,
            value,
            expected: expected || undefined,
            state,
        };
    });
};

const findFromCursor = (source: string, needle: string, cursor: number): number => {
    const direct = source.indexOf(needle, cursor);
    if (direct >= 0) return direct;
    return source.toLowerCase().indexOf(needle.toLowerCase(), cursor);
};

export const buildWritingTextAnnotationSegments = (
    answerText: string,
    correctedVersion: string,
): Pick<AnswerAnnotationModel, "segments" | "insertions" | "hasInlineAnnotations"> => {
    const source = normalizeAnswerText(answerText);
    const tokens = parseCorrectedTextTokens(correctedVersion);
    const segments: AnswerTextAnnotationSegment[] = [];
    const insertions: string[] = [];
    let cursor = 0;
    let hasInlineAnnotations = false;
    let annotationIndex = 0;

    tokens.forEach((token) => {
        if (token.type === "text") {
            if (!token.text) return;
            let matchedText = token.text;
            let start = findFromCursor(source, matchedText, cursor);
            if (start < 0 && token.text.trim()) {
                matchedText = token.text.trim();
                start = findFromCursor(source, matchedText, cursor);
            }
            if (start < 0) return;
            if (start > cursor) {
                segments.push({
                    text: source.slice(cursor, start),
                    state: "plain",
                    start: cursor,
                    end: start,
                });
            }
            const end = start + matchedText.length;
            segments.push({
                text: source.slice(start, end),
                state: "plain",
                start,
                end,
            });
            cursor = end;
            return;
        }

        if (token.type === "insertion") {
            if (token.correct.trim()) {
                segments.push({
                    text: token.correct.trim(),
                    state: "insertion",
                    start: cursor,
                    end: cursor,
                    annotationIndex: ++annotationIndex,
                });
                hasInlineAnnotations = true;
            }
            return;
        }

        const wrong = token.wrong.trim();
        if (!wrong) return;

        const start = findFromCursor(source, wrong, cursor);
        if (start < 0) return;

        if (start > cursor) {
            segments.push({
                text: source.slice(cursor, start),
                state: "plain",
                start: cursor,
                end: start,
            });
        }

        const end = start + wrong.length;
        segments.push({
            text: source.slice(start, end),
            state: "issue",
            start,
            end,
            annotationIndex: ++annotationIndex,
            correction: token.type === "replacement" ? token.correct : undefined,
        });
        cursor = end;
        hasInlineAnnotations = true;
    });

    if (cursor < source.length) {
        segments.push({
            text: source.slice(cursor),
            state: "plain",
            start: cursor,
            end: source.length,
        });
    }

    if (segments.length === 0 && source) {
        segments.push({
            text: source,
            state: "plain",
            start: 0,
            end: source.length,
        });
    }

    return {
        segments,
        insertions,
        hasInlineAnnotations: hasInlineAnnotations || insertions.length > 0,
    };
};

export const buildAnswerAnnotationModel = (
    answer: ModuleAnswer,
    expectedAnswers: readonly string[],
    options: { readonly includeFillInBlank?: boolean } = {},
): AnswerAnnotationModel => {
    if (options.includeFillInBlank !== false && isFillInBlankAnswer(answer)) {
        const blanks = buildWritingBlankAnnotations(
            normalizeAnswerText(answer.answer_text),
            expectedAnswers,
        );
        return {
            kind: "fill-in-blank",
            hasInlineAnnotations: blanks.length > 0,
            blanks,
            segments: [],
            insertions: [],
        };
    }

    const { segments, insertions, hasInlineAnnotations } = buildWritingTextAnnotationSegments(
        normalizeAnswerText(answer.answer_text),
        normalizeAnswerText(getCorrectedVersion(answer)),
    );

    return {
        kind: "text",
        hasInlineAnnotations,
        blanks: [],
        segments,
        insertions,
    };
};

export const buildWritingAnswerAnnotationModel = buildAnswerAnnotationModel;

const getContextKey = (answer: ModuleAnswer): string | null => {
    if (answer.context_id !== undefined && answer.context_id !== null) {
        return `id:${answer.context_id}`;
    }
    if (answer.context_name) {
        return `name:${answer.context_name.trim().toLowerCase()}`;
    }
    if (answer.context_content) {
        return `content:${answer.context_content}`;
    }
    if (answer.context_audio_base64) {
        return `audio:${answer.context_audio_base64}`;
    }
    if (answer.context_image_base64) {
        return `image:${answer.context_image_base64}`;
    }
    if (answer.context_media_url) {
        return `url:${answer.context_media_url}`;
    }
    return null;
};

const compareAnswers = (a: ModuleAnswer, b: ModuleAnswer): number => {
    const sectionDiff = (a.section || 0) - (b.section || 0);
    if (sectionDiff !== 0) return sectionDiff;
    // Prefer the order the learner actually saw during the (possibly shuffled)
    // exam. Both must have it to compare meaningfully; otherwise fall back to
    // the authored order so legacy answers keep their previous ordering.
    if (a.display_order != null && b.display_order != null) {
        const displayDiff = a.display_order - b.display_order;
        if (displayDiff !== 0) return displayDiff;
    }
    const orderDiff = (a.question_order || 0) - (b.question_order || 0);
    if (orderDiff !== 0) return orderDiff;
    return a.question_text.localeCompare(b.question_text);
};

export const buildAnswerGroups = (answers: readonly ModuleAnswer[]): AnswerGroups => {
    const contextGroups: ContextGroup[] = [];
    const standaloneAnswers: ModuleAnswer[] = [];
    const contextMap = new Map<string, ContextGroup>();

    answers.forEach((answer) => {
        const contextKey = getContextKey(answer);

        if (!contextKey) {
            standaloneAnswers.push(answer);
            return;
        }

        if (!contextMap.has(contextKey)) {
            contextMap.set(contextKey, {
                contextKey,
                contextInfo: {
                    name: answer.context_name || undefined,
                    type: answer.context_type || undefined,
                    content: answer.context_content || undefined,
                    audioBase64: answer.context_audio_base64 || undefined,
                    imageBase64: answer.context_image_base64 || undefined,
                    mediaUrl: answer.context_media_url || undefined,
                    transcript: answer.context_transcript || undefined,
                    mimeType: answer.context_mime_type || undefined,
                },
                answers: [],
            });
        }

        contextMap.get(contextKey)?.answers.push(answer);
    });

    contextMap.forEach((group) => {
        group.answers.sort(compareAnswers);
    });
    standaloneAnswers.sort(compareAnswers);

    contextGroups.push(...Array.from(contextMap.values()));
    contextGroups.sort((a, b) => compareAnswers(a.answers[0], b.answers[0]));

    return {
        contextGroups,
        standaloneAnswers,
        displayAnswers: [
            ...contextGroups.flatMap((group) => group.answers),
            ...standaloneAnswers,
        ],
    };
};

export const buildQuestionNavigatorItems = (
    displayAnswers: readonly ModuleAnswer[],
): QuestionNavigatorItem[] => displayAnswers.map((answer, idx) => {
    const isUnanswered = !hasSubmittedAnswer(answer);

    return {
        answer,
        number: idx + 1,
        isUnanswered,
        isCorrect: answer.is_correct === true,
    };
});

/** Human-readable mismatch descriptions for navigator vs display order (empty = aligned). */
export const getNavigatorAlignmentMismatches = (
    displayAnswers: readonly ModuleAnswer[],
    navigatorItems: readonly QuestionNavigatorItem[],
): string[] => {
    const mismatches: string[] = [];

    if (navigatorItems.length !== displayAnswers.length) {
        mismatches.push(
            `count: navigator ${navigatorItems.length} vs display ${displayAnswers.length}`,
        );
        return mismatches;
    }

    navigatorItems.forEach((item, index) => {
        const display = displayAnswers[index];
        if (item.answer.id !== display.id) {
            mismatches.push(`index ${index}: id ${item.answer.id} vs ${display.id}`);
        }
        if (item.number !== index + 1) {
            mismatches.push(`index ${index}: number ${item.number} vs ${index + 1}`);
        }
        const expectedCorrect = display.is_correct === true;
        if (item.isCorrect !== expectedCorrect) {
            mismatches.push(
                `index ${index}: isCorrect ${item.isCorrect} vs ${expectedCorrect}`,
            );
        }
    });

    return mismatches;
};

/** Flat card render order from grouped answers (matches ModuleAnswerReviewContent card list). */
export const getCardRenderOrder = (groups: AnswerGroups): string[] => [
    ...groups.contextGroups.flatMap((group) => group.answers.map((a) => a.id)),
    ...groups.standaloneAnswers.map((a) => a.id),
];

/**
 * Groups answers and section feedback by exam section for interleaved R/L review:
 * questions for section N, then feedback for section N, then section N+1, etc.
 */
export const buildSectionReviewBlocks = (
    groupedAnswers: AnswerGroups,
    sectionFeedback: readonly SectionFeedback[],
): SectionReviewBlock[] => {
    const sectionNumbers = new Set<number>();
    groupedAnswers.displayAnswers.forEach((answer) => {
        sectionNumbers.add(answer.section ?? 0);
    });
    sectionFeedback.forEach((row) => {
        sectionNumbers.add(row.section);
    });

    const feedbackBySection = new Map(
        sectionFeedback.map((row) => [row.section, row]),
    );

    return [...sectionNumbers]
        .sort((a, b) => a - b)
        .map((section) => {
            const contextGroups = groupedAnswers.contextGroups
                .map((group) => ({
                    ...group,
                    answers: group.answers.filter((answer) => (answer.section ?? 0) === section),
                }))
                .filter((group) => group.answers.length > 0);

            const standaloneAnswers = groupedAnswers.standaloneAnswers.filter(
                (answer) => (answer.section ?? 0) === section,
            );

            return {
                section,
                contextGroups,
                standaloneAnswers,
                sectionFeedback: feedbackBySection.get(section) ?? null,
            };
        })
        .filter(
            (block) =>
                block.contextGroups.length > 0
                || block.standaloneAnswers.length > 0
                || (block.sectionFeedback != null && sectionFeedbackRowHasContent(block.sectionFeedback)),
        );
};
